/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2021, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { appConstants } from '@app/common/app-constants';
import { PostMessengerService } from '@app/di/channel/window/post-messenger.service';
import { NPRAuthentication } from '@app/di/security/npr-authentication.service';

export const enum MessageType {
  Create = 'createReport',
  CreateUnspecified = 'createReportUnspecified',
  Open = 'openReport',
  Discard = 'discardReportChanges',
  Close = 'closeReport',
  Save = 'saveReport',
  Addend = 'addendReport',
  AddendLegacyReport = 'addendLegacyReport',
  Delete = 'deleteReport',
  SetTheme = 'setTheme',
  OpenPreferences = 'openUserSettings',
  UpdateToken = 'updateToken',
  OpenAdminPanel = 'openAdminPanel',
  AddToAssociation = 'addToAssociation',
  RemoveFromAssociation = 'removeFromAssociation'
}

export const SRClientReportOperations = [
  MessageType.Create,
  MessageType.CreateUnspecified,
  MessageType.Open,
  MessageType.Save,
  MessageType.Discard,
  MessageType.Delete,
  MessageType.Close,
  MessageType.Addend,
  MessageType.AddendLegacyReport,
  MessageType.AddToAssociation,
  MessageType.RemoveFromAssociation
];

export const enum SRClientError {
  SR_SERVICE_TIMEOUT = 'sr-service-timeout',
  SR_RESPONSE_UNPROCESSABLE = 'sr-response-unprocessable',
  SR_SERVICE_UNREACHABLE = 'notification-url-is-unreachable'
}

const NORMAL_REPORT_STATUS = {
  pending: 7100,
  draft: 7500,
  prelim: 7000,
  signed: 9000
};

const ADDENDUM_REPORT_STATUS = {
  pending: 9001,
  draft: 9500,
  prelim: 9900,
  signed: 9000
};

interface IMessageCore {
  type: MessageType;
  token: string;
  requestId: string;
}

interface IMessageData {
  [key: string]: any;
}

type Message = IMessageCore | IMessageData;

const log = new Logger('SRClientService');

export class SRClientService {
  private static readonly WINDOW_OPTIONS = 'toolbar=no,scrollbars=no,location=no,status=no,menubar=no,resizable=yes';

  private static requestTimeout = 60_000;

  private static ready = false;

  private static instances = {};

  private static target = '';

  private static language = 'en-US';

  private static theme = 'dark';

  private appId = '';

  private storageId = '';

  private handler: any = null;

  private resolver = {};

  //#region customizable event handlers

  public onready: any = null;

  public onevent: any = null;

  public onerror: any = null;

  public onclose: any = null;

  //#endregion

  constructor(
    private name: string,
    private authenticator: NPRAuthentication,
    private postMessengerService: PostMessengerService
  ) {
    SRClientService.instances[name] = this;
    this.storageId = 'GE/NPR' + name;
    this.appId = appConstants.APP_ID.SR + '.' + this.name;

    postMessengerService.register({
      targetAppId: this.appId,
      onResponse: data => SRClientService.eventHandler(data)
    });
  }

  public static register(target: string, language: string, theme: string, timeout: number) {
    if (!SRClientService.ready) {
      SRClientService.target = target;
      SRClientService.language = language;
      SRClientService.theme = theme;
      SRClientService.requestTimeout = timeout;
      SRClientService.ready = true;
    }
  }

  private static eventHandler(event: any) {
    const client: SRClientService = SRClientService.instances[event.$target];
    if (!!client) {
      const response = client.resolver[event.requestId];
      if (response) {
        delete client.resolver[event.requestId];
        if (response.timer) {
          clearTimeout(response.timer);
        }
      } else if (event.requestId) {
        // An event that has valid requestId but no resolver/response is most likely timedout,
        // this event should not be processed
        if (client.onerror !== null) {
          client.onerror({ code: 422, errorCode: SRClientError.SR_RESPONSE_UNPROCESSABLE, event: event });
        }
        return;
      }
      log.debug('NPRClient got event', event);
      if (event.applicationStatus === 'READY') {
        if (client.handler && client.handler.onClientReady !== null) {
          client.handler.onClientReady();
          client.handler.onClientReady = null;
        }
        if (client.onready !== null) {
          client.onready();
        }
      } else if (event.code >= 400) {
        log.error(client.name, ' got error ', event);
        if (client.onerror !== null) {
          client.onerror(event);
        }
        if (response && response.fail) {
          response.fail(event);
        }
      } else {
        log.debug(client.name, ' got event ', event);
        if (event.reportStatus) {
          const statusName: string = event.reportStatus;
          event.reportStatus = event.isAddended ? ADDENDUM_REPORT_STATUS[statusName] : NORMAL_REPORT_STATUS[statusName];
          log.debug(client.name, ' got event after status conversion', event);
        }
        if (client.onevent !== null) {
          client.onevent(event);
        }
        if (response && response.ok) {
          response.ok(event);
        }
      }
    }
  }

  private hasValidHandler(): boolean {
    const valid = this.handler !== null && !this.handler.closed;
    log.debug(this.name, ' is valid: ', valid);
    return valid;
  }

  private loadDimension() {
    let bounds = localStorage.getItem(this.storageId);
    if (!bounds) {
      const w = 1280;
      const h = 768;
      const x = (window.screen.width - w) / 2;
      const y = (window.screen.height - h) / 2;
      bounds = `, left=${x}, top=${y}, width=${w}, height=${h}`;
      localStorage.setItem(this.storageId, bounds);
    }
    return bounds;
  }

  private saveDimension() {
    if (this.handler) {
      const x = this.handler.screenLeft;
      const y = this.handler.screenTop;
      const w = this.handler.innerWidth;
      const h = this.handler.innerHeight;
      localStorage.setItem(this.storageId, `, left=${x}, top=${y}, width=${w}, height=${h}`);
    }
  }

  //#region window handling function
  public show() {
    if (this.hasValidHandler()) {
      try {
        log.debug(this.name, ' try to grab focus');
        this.handler.showContents();
      } catch (error) {
        log.error('Failed to focus on ' + this.name, ':', error);
        return Promise.reject(false);
      }
      log.debug(this.name, ' has focus');
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve, reject) => {
      const dim = this.loadDimension();
      this.handler = window.open('report-client.html', this.name, SRClientService.WINDOW_OPTIONS + dim);
      log.debug(this.name, ' is setting callback handlers');
      // onClientReady is triggered when this NPR client receives message applicationStatus = 'READY' (see eventHandler)
      this.handler.onClientReady = () => {
        log.debug('SmartReporting UI is READY');
        resolve(true);
      };
      // onReadyToStart is triggered when report-client.html finishes setting up message handler
      this.handler.onReadyToStart = () => {
        const saveDimensionHandler = () => this.saveDimension();
        this.handler.onresize = saveDimensionHandler;
        this.handler.onblur = saveDimensionHandler;
        this.handler.onbeforeunload = () => {
          log.debug(this.name, 'is closing');
          this.saveDimension();
          this.handler = null;
          if (this.onclose !== null) {
            this.onclose();
          }
        };
        // onClientLoaded is triggered after report-client.html finishes loading editor from SR service
        this.handler.onClientLoaded = () => {
          log.debug('SmartReporting UI loaded');
        };
        // onClientError is triggered if report-client.html fails to load editor from SR service
        this.handler.onClientError = () => {
          log.debug('SmartReporting UI failed to load');
          reject(false);
        };
        log.debug('Loading SmartReporting UI');
        this.start();
      };
    });
  }

  public start() {
    if (this.hasValidHandler()) {
      const url = SRClientService.target + '?locale=' + SRClientService.language + '&theme=' + SRClientService.theme;
      this.handler.start(this.name, url);
    }
  }

  public showContents() {
    if (this.hasValidHandler()) {
      this.handler.showContents();
      this.handler.focus();
    }
  }

  public hideContents() {
    if (this.hasValidHandler()) {
      this.handler.hideContents();
    }
  }

  public focus() {
    this.handler?.focus();
  }

  public blur() {
    this.handler?.blur();
  }

  public close() {
    this.handler?.close();
    this.postMessengerService.unregister(this.appId);
  }
  //#endregion

  protected createMessage(type: string, ...objs: any[]) {
    let message: Message = {
      type: type,
      token: this.authenticator.getToken(),
      requestId: this.authenticator.createUUID()
    };
    objs.forEach(obj => {
      message = { ...message, ...obj };
    });
    return message;
  }

  protected sendRequest(type: MessageType, ...data: any[]) {
    if (this.hasValidHandler()) {
      const message = this.createMessage(type, ...data);
      log.debug(this.name, 'prepares to send', message);

      this.resolver[message.requestId] = { ok: undefined, fail: undefined, timer: undefined };
      return new Promise<any>((resolve, reject) => {
        log.debug(this.name, 'sends', message);
        this.resolver[message.requestId].ok = resolve;
        this.resolver[message.requestId].fail = reject;
        if (message.type !== MessageType.Create && message.type !== MessageType.CreateUnspecified) {
          this.resolver[message.requestId].timer = setTimeout(() => {
            log.debug(this.name, 'request timedout', message);
            message.token = '...';
            const error = { code: 408, errorCode: SRClientError.SR_SERVICE_TIMEOUT, message: message };
            delete this.resolver[message.requestId];
            if (this.onerror) {
              this.onerror(error);
            }
            reject(error);
          }, SRClientService.requestTimeout);
        }
        this.handler.sendRequest(message);
      });
    }
    return Promise.resolve(`${this.name}'s client window is unavailable`);
  }

  public syncToken() {
    return this.sendRequest(MessageType.UpdateToken).catch(error => log.error('update token failed : ', error));
  }

  public setTheme(theme: string) {
    SRClientService.theme = theme;
    if (this.hasValidHandler()) {
      this.handler.setTheme(theme);
    }
    return this.sendRequest(MessageType.SetTheme, { themeName: theme }).catch(error =>
      log.error('setting theme failed : ', error)
    );
  }

  public showWarning(message: string) {
    if (this.hasValidHandler()) {
      this.handler.showWarning(message);
    }
  }

  public hideWarning() {
    if (this.hasValidHandler()) {
      this.handler.hideWarning();
    }
  }
  /**
   * This temporary API is used as workaround solution for the missing associated exams in openReport's response
   * This will be removed in future when proper fix is done on SmartReporting
   */
  public getReportAssociatedExams(resourceService: any, reportId: string) {
    const url = SRClientService.target + `/api/v1/report/reports/${reportId}/orders`;
    return resourceService.getResource(url);
  }
}
